import React, { useEffect, useMemo, useState } from "react";
import CreatableSelect from "react-select/creatable";
import "../AddWorkTypeModal/AddWorkTypeModal.css";

const normalizeName = (value) => String(value || "").trim().toLowerCase();

const selectStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: 44,
    borderRadius: 10,
    borderColor: state.isFocused ? "#2a5e38" : "#d1d5db",
    boxShadow: "none",
    "&:hover": { borderColor: state.isFocused ? "#2a5e38" : "#9ca3af" },
  }),
  multiValue: (provided) => ({
    ...provided,
    background: "#eef5f0",
    borderRadius: 8,
  }),
  multiValueLabel: (provided) => ({
    ...provided,
    color: "#214a2c",
    fontSize: 13,
  }),
  menuPortal: (provided) => ({
    ...provided,
    zIndex: 1100,
  }),
  option: (provided, state) => ({
    ...provided,
    background: state.isSelected
      ? "#2a5e38"
      : state.isFocused
        ? "#eef5f0"
        : "#fff",
    color: state.isSelected ? "#fff" : "#111827",
  }),
};

const TemplateAddItemModal = ({
  open,
  isMilestone = false,
  existingNames = [],
  suggestedNames = [],
  onClose,
  onAdd,
}) => {
  const [options, setOptions] = useState([]);
  const [selected, setSelected] = useState([]);
  const [error, setError] = useState("");

  const existingSet = useMemo(
    () => new Set(existingNames.map(normalizeName)),
    [existingNames],
  );

  useEffect(() => {
    if (!open) return;
    setError("");
    setSelected([]);
    const existing = new Set(existingNames.map(normalizeName));
    setOptions(
      (suggestedNames || [])
        .filter((name) => name && !existing.has(normalizeName(name)))
        .map((name) => ({ value: name, label: name })),
    );
  }, [open, existingNames, suggestedNames]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  const isValidNewOption = (inputValue) => {
    const trimmed = String(inputValue || "").trim();
    if (!trimmed) return false;
    const key = normalizeName(trimmed);
    if (existingSet.has(key)) return false;
    if (selected.some((opt) => normalizeName(opt.value) === key)) return false;
    if (options.some((opt) => normalizeName(opt.value) === key)) return false;
    return true;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const names = selected
      .map((opt) => String(opt.value || "").trim())
      .filter(Boolean);
    if (names.length === 0) {
      setError(isMilestone ? "Выберите или создайте веху" : "Выберите виды работ");
      return;
    }
    onAdd(names);
    onClose();
  };

  return (
    <>
      <div
        className="add-work-type-modal-backdrop"
        onClick={onClose}
      />
      <div
        className="add-work-type-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="template-add-item-title"
      >
        <h2 id="template-add-item-title" className="add-work-type-modal__title">
          {isMilestone ? "Добавление вехи" : "Добавление вида работ"}
        </h2>
        <div className="add-work-type-modal__hint">
          <span className="add-work-type-modal__hint-icon" aria-hidden>
            ⚠
          </span>
          <p>
            {isMilestone
              ? "В шаблоне веха — это контрольная точка. Плановую дату укажете уже на проекте."
              : "После применения шаблона к проекту можно будет уточнить веса и сроки."}
          </p>
        </div>
        <form onSubmit={handleSubmit}>
          <label className="add-work-type-modal__label">
            {isMilestone
              ? "Выберите веху или создайте новую"
              : "Выберите виды работ или создайте новый"}
          </label>
          <CreatableSelect
            isMulti={!isMilestone}
            isClearable
            isSearchable
            options={options}
            value={isMilestone ? selected[0] || null : selected}
            onChange={(value) => {
              setError("");
              if (isMilestone) {
                setSelected(value ? [value] : []);
              } else {
                setSelected(value || []);
              }
            }}
            styles={selectStyles}
            menuPortalTarget={document.body}
            placeholder="Начните вводить название..."
            noOptionsMessage={() => "Нет вариантов — введите название, чтобы создать"}
            formatCreateLabel={(input) => `Создать «${input.trim()}»`}
            isValidNewOption={isValidNewOption}
            onCreateOption={(inputValue) => {
              const name = String(inputValue || "").trim();
              if (!isValidNewOption(name)) {
                if (existingSet.has(normalizeName(name))) {
                  setError(
                    isMilestone
                      ? "Такая веха уже есть в этом разделе"
                      : "Такой вид работ уже есть в этом разделе",
                  );
                }
                return;
              }
              const option = { value: name, label: name, __isNew__: true };
              setOptions((prev) => [...prev, option]);
              setSelected((prev) => (isMilestone ? [option] : [...prev, option]));
            }}
          />
          {error && <p className="add-work-type-modal__error">{error}</p>}
          <div className="add-work-type-modal__actions">
            <button
              type="button"
              className="add-work-type-modal__btn add-work-type-modal__btn--cancel"
              onClick={onClose}
            >
              Отмена
            </button>
            <button
              type="submit"
              className="add-work-type-modal__btn add-work-type-modal__btn--submit"
              disabled={selected.length === 0}
            >
              Добавить
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

export default TemplateAddItemModal;
